import moment from "moment";
import { formatExactQty } from "../Shared/formatters";
export { API_DATE } from "../Shared/formatters";

// Quantities (credited `quantity`, `penalty`, `net`, payable `adjusted`) can
// come as a number (shift basis) or a duration "HH:MM" (hours basis, e.g.
// "08:15"). Durations are shown as sent and
// count as decimal hours (08:15 = 8.25) in totals and rate maths.
const DURATION = /^(\d+):([0-5]\d)$/;
export const isDuration = (value) => typeof value === "string" && DURATION.test(value.trim());
export const qtyNumber = (value) => {
  if (isDuration(value)) {
    const [, hours, minutes] = value.trim().match(DURATION);
    return Number(hours) + Number(minutes) / 60;
  }
  const number = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(number) ? null : number;
};
export const formatPayableQty = (value) => (isDuration(value) ? value.trim() : formatExactQty(value));
const toDuration = (hours) => {
  const minutes = Math.round(hours * 60);
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
};
// Card total: all durations -> "HH:MM"; otherwise a decimal sum.
export const sumPayableQty = (rows, key = "adjusted") => {
  const values = rows.map((row) => row[key]).filter((value) => value !== null && value !== undefined && value !== "");
  const total = values.reduce((acc, value) => acc + (qtyNumber(value) || 0), 0);
  if (values.length && values.every(isDuration)) return toDuration(total);
  return (Math.round(total * 100) / 100).toFixed(2);
};
// Same values as GET payroll/earning/shift/earning/types/ (the Add/Edit form
// loads that list; this copy is its fallback and what the import accepts).
export const DAY_TYPES = ["Week Day", "Weekoff Attendance", "Holiday Attendance", "WeekOff", "Paid WeekOff", "Holiday", "Paid Holiday", "Absent", "Paid Absent", "Leave", "Paid Leave", "Company Leave"];
export const BASIS_OPTIONS = [{ value: "shift", label: "Shift" }, { value: "hours", label: "Hours" }];
export const APPROVAL_STATUSES = ["Pending", "Approved", "Rejected"];
export const emptyForm = () => ({ employee_code: "", date: moment().format("YYYY-MM-DD"), earning_type: "Week Day", shift_name: "", worked_shift_hours: "", penalty: "0", basis: "shift", quantity: "", payment_factor: "1", rate: "", pay_schedule_id: "", status: "Pending" });
export const rowToForm = (row) => ({ employee_code: row.employee_code, employee_name: row.employee_name, image_url: row.image_url || "", department_name: row.department_name || row.department || "", designation: row.designation_name || row.designation || "", date: row.date, earning_type: row.earning_type, shift_name: row.shift_name || "", worked_shift_hours: row.worked_shift_hours || "", penalty: String(row.penalty ?? ""), basis: row.basis, quantity: String(row.quantity ?? ""), payment_factor: String(row.payment_factor ?? ""), rate: String(row.rate ?? ""), pay_schedule_id: row.pay_schedule_id == null ? "" : String(row.pay_schedule_id), status: row.status, net: row.net, adjusted: row.adjusted, payable_amount: row.payable_amount });

// A preview never replaces server amounts or enters the save payload.
export const previewShiftEarning = (form, initial) => {
  if (initial && ["quantity", "payment_factor", "rate"].every((key) => form[key] === initial[key])) {
    return { net: initial.net, adjusted: initial.adjusted, payable_amount: initial.payable_amount };
  }
  const quantity = Number(form.quantity) || 0;
  const adjusted = quantity * (Number(form.payment_factor) || 0);
  return { net: quantity, adjusted, payable_amount: adjusted * (Number(form.rate) || 0) };
};
export const validateForm = (form, initial) => {
  const errors = {};
  if (!form.employee_code.trim()) errors.employee_code = "Enter an employee code.";
  if (!moment(form.date, "YYYY-MM-DD", true).isValid()) errors.date = "Enter a valid date.";
  if (!["shift", "hours"].includes(form.basis)) errors.basis = "Select a basis.";
  if (String(form.worked_shift_hours ?? "").trim() && !/^\d{1,2}:[0-5]\d$/.test(String(form.worked_shift_hours).trim())) errors.worked_shift_hours = "Enter hours as HH:MM.";
  for (const key of ["quantity", "rate", "payment_factor"]) {
    if (String(form[key]).trim() === "" || !Number.isFinite(Number(form[key])) || Number(form[key]) < 0) errors[key] = "Enter a number of 0 or more.";
  }
  if (initial) Object.keys(errors).forEach((key) => { if (form[key] === initial[key]) delete errors[key]; });
  return errors;
};
